/**
 * BanditContext — session cache of bandit posterior snapshots and lifecycle
 * control docs for every experiment registered for the current course.
 *
 * Mounted inside `Authenticated` (next to `ExperimentProvider`). Fetches once
 * per course and keeps the snapshot frozen for the session: assignments are
 * sticky, so mid-session posterior updates must never re-sample anyone.
 * Courses with no registered experiments cost zero reads.
 *
 * After loading, stale snapshots trigger a fire-and-forget call to the
 * `refreshbanditposterior` function — the demand-driven replacement for a
 * cron. See the @cip/bandit README for the architecture.
 */

import {
  createContext,
  useContext,
  useEffect,
  useState,
  type ReactNode,
} from 'react';
import {doc, getDoc, getFirestore} from 'firebase/firestore';
import {
  banditControlSchema,
  banditSnapshotSchema,
  banditControlDocPath,
  banditSnapshotDocPath,
  getExperimentsForCourse,
  type BanditControl,
  type BanditExperimentDefinition,
  type BanditSnapshot,
  type ExperimentRef,
} from '@cip/bandit';
import {useCourseIdWithoutFallback} from 'hooks/router/useUrlParams';
import {requestBanditRefresh} from './refreshBanditPosteriorApi';
import {toMillisOrNull} from './firestoreTime';

/**
 * Snapshots older than this many milliseconds trigger a background refresh.
 * Staleness up to this bound is just batched Thompson sampling — harmless.
 */
const SNAPSHOT_STALE_MS = 30 * 60 * 1000;

/** Value provided by {@link BanditProvider}. */
export interface BanditContextValue {
  /**
   * True once snapshot/control fetches for the current course settled (also
   * true immediately for courses with no registered experiments). While
   * false, `useBanditArm` samples from the prior rather than waiting.
   */
  loaded: boolean;
  /** experimentId → parsed snapshot, or null when absent/invalid. */
  snapshots: Record<string, BanditSnapshot | null>;
  /** experimentId → parsed control doc, or null when absent (= active). */
  controls: Record<string, BanditControl | null>;
}

const DEFAULT_VALUE: BanditContextValue = {
  loaded: false,
  snapshots: {},
  controls: {},
};

const BanditContext = createContext<BanditContextValue>(DEFAULT_VALUE);

/** One experiment's cached docs; null means absent or unreadable. */
interface ExperimentDocs {
  snapshot: BanditSnapshot | null;
  control: BanditControl | null;
}

/**
 * Reads one experiment's posterior snapshot and lifecycle control doc.
 *
 * @param ref - The experiment.
 * @return Both docs, parsed; a failed read or malformed doc yields null.
 * @throws Never.
 */
async function fetchExperimentDocs(ref: ExperimentRef): Promise<ExperimentDocs> {
  const firestore = getFirestore();
  try {
    const [snapshotDoc, controlDoc] = await Promise.all([
      getDoc(doc(firestore, banditSnapshotDocPath(ref))),
      getDoc(doc(firestore, banditControlDocPath(ref))),
    ]);
    const parsedSnapshot = banditSnapshotSchema.safeParse(snapshotDoc.data());
    const parsedControl = banditControlSchema.safeParse(controlDoc.data());
    return {
      snapshot: parsedSnapshot.success ? parsedSnapshot.data : null,
      control: parsedControl.success ? parsedControl.data : null,
    };
  } catch (e) {
    console.debug('[bandit] snapshot fetch failed', ref.experimentId, e);
    return {snapshot: null, control: null};
  }
}

/**
 * Asks the backend to recompute a live experiment's posterior when this
 * session's snapshot is missing or older than {@link SNAPSHOT_STALE_MS}.
 * Fire-and-forget; see the module docs.
 *
 * @param ref - The experiment.
 * @param docs - What this session read for it.
 * @return Nothing.
 * @throws Never.
 */
function refreshIfStale(ref: ExperimentRef, docs: ExperimentDocs): void {
  if (docs.control?.status === 'concluded') {
    return;
  }
  const computedAtMs = toMillisOrNull(docs.snapshot?.computedAt);
  const isStale =
    computedAtMs === null || Date.now() - computedAtMs > SNAPSHOT_STALE_MS;
  if (isStale) {
    requestBanditRefresh(ref);
  }
}

/**
 * Session cache for ONE experiment defined in data rather than in the code
 * registry, such as a lesson A/B test (#5230). The provider only knows the
 * registry, so `useBanditArm` reads a data-defined experiment's docs here
 * instead. Like the provider, it fetches once and keeps the snapshot frozen.
 *
 * @param definition - The experiment. When omitted, nothing is read and the
 *   result stays not-loaded.
 * @return `loaded` once the reads settle, plus the parsed docs.
 * @throws Never.
 */
export function useDataExperimentDocs(
  definition: BanditExperimentDefinition | undefined,
): ExperimentDocs & {loaded: boolean} {
  const courseId = definition?.courseId;
  const experimentId = definition?.experimentId;
  const [state, setState] = useState<ExperimentDocs & {loaded: boolean}>({
    loaded: false,
    snapshot: null,
    control: null,
  });

  useEffect(() => {
    if (!courseId || !experimentId) {
      return;
    }
    let cancelled = false;
    const ref = {courseId, experimentId};
    void fetchExperimentDocs(ref).then(docs => {
      if (cancelled) {
        return;
      }
      setState({loaded: true, ...docs});
      refreshIfStale(ref, docs);
    });
    return () => {
      cancelled = true;
    };
  }, [courseId, experimentId]);

  return state;
}

/**
 * Reads {@link BanditContext}.
 *
 * @return The current bandit cache value (defaults to not-loaded outside a
 *   provider, which makes `useBanditArm` fall back to prior sampling).
 */
export function useBanditContext(): BanditContextValue {
  return useContext(BanditContext);
}

/**
 * Provider component; see module docs.
 *
 * @param props.children - Subtree that may call `useBanditArm`.
 * @return Provider element.
 */
export function BanditProvider({children}: {children: ReactNode}) {
  const courseId = useCourseIdWithoutFallback();
  const [value, setValue] = useState<BanditContextValue>(DEFAULT_VALUE);

  useEffect(() => {
    const experiments = courseId ? getExperimentsForCourse(courseId) : [];
    if (experiments.length === 0) {
      setValue({loaded: true, snapshots: {}, controls: {}});
      return;
    }
    let cancelled = false;
    void (async () => {
      const snapshots: Record<string, BanditSnapshot | null> = {};
      const controls: Record<string, BanditControl | null> = {};
      await Promise.all(
        experiments.map(async experiment => {
          const docs = await fetchExperimentDocs(experiment);
          snapshots[experiment.experimentId] = docs.snapshot;
          controls[experiment.experimentId] = docs.control;
        }),
      );
      if (cancelled) {
        return;
      }
      setValue({loaded: true, snapshots, controls});

      // Demand-driven refresh: ask the backend to recompute stale posteriors.
      // Fire-and-forget — this session keeps sampling from the snapshot it
      // already has (batched Thompson sampling); the refresh serves the next
      // students to arrive.
      for (const experiment of experiments) {
        refreshIfStale(experiment, {
          snapshot: snapshots[experiment.experimentId] ?? null,
          control: controls[experiment.experimentId] ?? null,
        });
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [courseId]);

  return (
    <BanditContext.Provider value={value}>{children}</BanditContext.Provider>
  );
}
